import React, { useState, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Label,
  Input,
  Table,
  Badge,
  Alert,
} from "reactstrap";

const PTEScoreCalculator = () => {
  const [scores, setScores] = useState({
    listening: "",
    reading: "",
    writing: "",
    speaking: "",
  });

  const handleChange = (field, value) => {
    if (value === "") {
      setScores({ ...scores, [field]: "" });
      return;
    }
    const num = parseInt(value);
    if (num >= 0 && num <= 90) {
      setScores({ ...scores, [field]: value });
    }
  };

  const averageScore = useMemo(() => {
    const vals = Object.values(scores)
      .filter((v) => v !== "")
      .map(Number);
    if (vals.length === 0) return null;
    return (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
  }, [scores]);

  const getIELTSBand = (pteScore) => {
    if (!pteScore) return null;
    const s = Math.round(parseFloat(pteScore));
    if (s >= 86) return 9.0;
    if (s >= 83) return 8.5;
    if (s >= 79) return 8.0;
    if (s >= 73) return 7.5;
    if (s >= 65) return 7.0;
    if (s >= 59) return 6.5;
    if (s >= 51) return 6.0;
    if (s >= 43) return 5.5;
    if (s >= 35) return 5.0;
    return 4.5;
  };

  const equivalentBand = getIELTSBand(averageScore);

  // ✅ EXACT table as shown in image
  const bandTable = [
    { pte: "86–90", ielts: "9.0", level: "Expert" },
    { pte: "83–85", ielts: "8.5", level: "Very Good" },
    { pte: "79–82", ielts: "8.0", level: "Very Good" },
    { pte: "73–78", ielts: "7.5", level: "Good" },
    { pte: "65–72", ielts: "7.0", level: "Good" },
    { pte: "59–64", ielts: "6.5", level: "Competent" },
    { pte: "51–58", ielts: "6.0", level: "Competent" },
    { pte: "43–50", ielts: "5.5", level: "Modest" },
    { pte: "35–42", ielts: "5.0", level: "Modest" },
  ];

  return (
    <div style={{ background: "#f8f9fa", minHeight: "100vh", padding: "2rem 0" }}>
      <Container style={{ maxWidth: "900px" }}>
        {/* Header */}
        <Card className="border-0 shadow-sm mb-4">
          <CardBody
            className="text-center py-4"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            }}
          >
            <h1 className="text-white fw-bold mb-2">
              PTE Score Calculator with IELTS Conversion
            </h1>
            <p className="text-white mb-0" style={{ opacity: 0.9 }}>
              Calculate your PTE Academic scores with equivalent IELTS Score
            </p>
          </CardBody>
        </Card>

        {/* Input Section */}
        <Card className="border-0 shadow-sm mb-4">
          <CardBody className="p-4">
            <Label className="fw-bold text-uppercase text-muted mb-3">
              Enter Your PTE Scores
            </Label>

            <Row className="g-3">
              {Object.keys(scores).map((skill) => (
                <Col md={6} key={skill}>
                  <FormGroup>
                    <Label className="fw-semibold text-capitalize">
                      {skill}
                    </Label>
                    <Input
                      type="number"
                      placeholder="0 – 90"
                      value={scores[skill]}
                      onChange={(e) => handleChange(skill, e.target.value)}
                      className="py-2"
                    />
                  </FormGroup>
                </Col>
              ))}
            </Row>

            <div className="mt-3 p-3 bg-light rounded border-start border-4 border-primary">
              <small className="text-muted">
                Overall PTE score is averaged from all four sections.
              </small>
            </div>
          </CardBody>
        </Card>

        {/* Result Section (UNCHANGED + RESTORED) */}
        <Card className="border-0 shadow-sm mb-4">
          <CardBody className="p-4">
            <Label className="fw-bold text-uppercase text-muted mb-3">
              Your Result
            </Label>

            <Alert color="primary" className="border-0 mb-0" style={{ background: "#f0f8ff" }}>
              <Row className="align-items-center">
                <Col md={6} className="text-center text-md-start mb-3 mb-md-0">
                  <div className="text-uppercase fw-semibold mb-1 small">
                    Overall PTE Score
                  </div>
                  <div style={{ fontSize: "2.5rem", fontWeight: "700" }}>
                    {averageScore ?? "--"}
                  </div>
                  <Badge color="primary" className="mt-2">
                    Equivalent IELTS Band
                  </Badge>
                </Col>

                <Col md={6}>
                  <Row className="g-3">
                    <Col lg={6}>
                      <div className="text-center bg-white rounded p-3">
                        <div className="text-muted small">Sections</div>
                        <div className="fw-bold fs-5">
                          {Object.values(scores).filter((v) => v !== "").length}/4
                        </div>
                      </div>
                    </Col>
                    <Col lg={6}>
                      <div className="text-center bg-white rounded p-3">
                        <div className="text-muted small">
                          Equivalent IELTS Band
                        </div>
                        <div className="fw-bold fs-5">
                          {equivalentBand ?? "--"}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Alert>
          </CardBody>
        </Card>

        {/* Reference Table (IMAGE MATCHING) */}
        <Card className="border-0 shadow-sm">
          <CardBody className="p-4">
            <Label className="fw-bold text-uppercase text-muted mb-3">
              IELTS Band Descriptors
            </Label>

            <div className="table-responsive">
              <Table hover className="align-middle mb-0">
                <thead className="bg-primary text-white">
                  <tr>
                    <th>PTE Score</th>
                    <th className="text-center">IELTS Band</th>
                    <th className="text-end">Proficiency Level</th>
                  </tr>
                </thead>
                <tbody>
                  {bandTable.map((row, i) => (
                    <tr key={i}>
                      <td className="fw-bold">{row.pte}</td>
                      <td className="text-center fw-semibold">
                        {row.ielts}
                      </td>
                      <td className="text-end text-muted">
                        {row.level}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>

        {/* Footer */}
        <div className="text-center mt-4">
          <p className="text-muted small mb-0">
            <strong>PTE Calculator with IELTS Conversion</strong>
          </p>
        </div>
      </Container>
    </div>
  );
};

export default PTEScoreCalculator;
